import type { MaybeRefOrGetter, Ref } from 'vue';
import { useMutationObserver, useResizeObserver } from '@vueuse/core';
import { computed, nextTick, onMounted, readonly, ref, toValue, watch } from 'vue';
import { calculateOverflow } from '#layers/ui/utils/calculate-overflow';

export interface UseOverflowOptions {
	containerRef: MaybeRefOrGetter<HTMLElement | null>;
	itemsRef?: MaybeRefOrGetter<HTMLElement | null>;
	itemCount: MaybeRefOrGetter<number>;
	itemSelector?: string;
	triggerRef?: MaybeRefOrGetter<HTMLElement | null>;
	estimatedTriggerWidth?: number;
	enabled?: MaybeRefOrGetter<boolean>;
	/** Additional element to observe for resize (e.g. a flex parent that grows when the container itself is content-sized). */
	observeRef?: MaybeRefOrGetter<HTMLElement | null>;
	/** Override how available width is derived from the container element. Defaults to `container.offsetWidth`. */
	getContainerWidth?: (container: HTMLElement) => number;
}

export interface UseOverflowReturn {
	splitIndex: Readonly<Ref<number>>;
	overflowCount: Readonly<Ref<number>>;
	hasOverflow: Readonly<Ref<boolean>>;
	triggerMaxWidth: Readonly<Ref<number | undefined>>;
	recalculate: () => void;
}

/**
 * Measures container and child item widths to determine how many items fit.
 * Uses `containerRef` for available width and `itemsRef` (or containerRef) to query children via `itemSelector`.
 */
export function useOverflow(options: UseOverflowOptions): UseOverflowReturn {
	const {
		containerRef,
		itemsRef,
		itemCount,
		itemSelector = ':scope > *',
		triggerRef,
		estimatedTriggerWidth = 80,
		enabled = true,
		observeRef,
		getContainerWidth,
	} = options;

	const splitIndex = ref(toValue(itemCount));
	const overflowCount = ref(0);
	const triggerMaxWidth = ref<number | undefined>();
	const hasOverflow = computed(() => overflowCount.value > 0);

	function recalculate() {
		const container = toValue(containerRef);
		const itemsContainer = toValue(itemsRef) ?? container;
		const count = toValue(itemCount);

		if (!toValue(enabled) || !container || !itemsContainer || count === 0) {
			splitIndex.value = count;
			overflowCount.value = 0;
			triggerMaxWidth.value = undefined;
			return;
		}

		const items = itemsContainer.querySelectorAll(itemSelector);
		const itemWidths = [...items].map((el) => (el as HTMLElement).offsetWidth);
		const trigger = toValue(triggerRef);
		const style = getComputedStyle(itemsContainer);
		const containerWidth = getContainerWidth?.(container) ?? container.offsetWidth;
		const gap = Number.parseFloat(style.columnGap) || 0;

		const result = calculateOverflow({
			containerWidth,
			itemWidths,
			triggerWidth: trigger?.offsetWidth || estimatedTriggerWidth,
			gap,
		});

		splitIndex.value = result.splitIndex;
		overflowCount.value = result.overflowCount;

		if (result.overflowCount === 0) {
			triggerMaxWidth.value = undefined;
			return;
		}

		let visibleItemsWidth = 0;

		for (let i = 0; i < result.splitIndex; i++) {
			if (i > 0) visibleItemsWidth += gap;
			visibleItemsWidth += itemWidths[i] ?? 0;
		}

		const triggerGap = result.splitIndex > 0 ? gap : 0;
		triggerMaxWidth.value = Math.max(0, containerWidth - visibleItemsWidth - triggerGap);
	}

	// Batch rapid resize events into one measurement per animation frame
	let rafId: number | null = null;

	function deferRecalculate() {
		if (rafId !== null) return;

		rafId = requestAnimationFrame(() => {
			rafId = null;
			recalculate();
		});
	}

	useResizeObserver(computed(() => toValue(containerRef)), deferRecalculate);

	useMutationObserver(computed(() => toValue(itemsRef) ?? toValue(containerRef)), deferRecalculate, {
		childList: true,
		characterData: true,
		subtree: true,
	});

	useResizeObserver(computed(() => toValue(triggerRef)), deferRecalculate);

	if (observeRef) {
		useResizeObserver(computed(() => toValue(observeRef)), deferRecalculate);
	}

	watch(() => toValue(itemCount), (newCount) => {
		splitIndex.value = newCount;
		overflowCount.value = 0;
		triggerMaxWidth.value = undefined;
		nextTick(recalculate);
	});

	watch(() => toValue(enabled), () => deferRecalculate());
	onMounted(() => nextTick(recalculate));

	return {
		splitIndex: readonly(splitIndex),
		overflowCount: readonly(overflowCount),
		hasOverflow: readonly(hasOverflow),
		triggerMaxWidth: readonly(triggerMaxWidth),
		recalculate,
	};
}
